import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["badge", "colorVariants", "freeDelivery", "hasMultiplePrices", "hasVariations", "imageHoverActive", "isPortrait", "isValueMessaging", "name", "partNumber", "price", "productInfoOnly", "rating", "reviewsCount", "specialOfferText", "wasPrice", "brand", "lazyImageLoad", "featured", "cta", "turnOffCta", "productLinkOverride", "wishlistCta", "colorSwatchInline", "colorSwatchAlwaysVisible", "colorSwatchMaxVariations", "colorSwatchSize", "mobileLayout", "onClick", "data-test", "availability", "energyRating", "parentPartNumber"];
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Image as BoltImage, Ratings, RectangleSkeleton, EnergyLabelBadge } from '@sainsburys-tech/bolt';
import { canUseDom } from '@sainsburys-tech/boltui-utils';
import { UnavailableIcon, AvailableIcon, CalendarIcon, ClockIcon, InfoIcon } from '@sainsburys-tech/bolt-icons';
import ProductImageSkeleton from './components/ProductImage.Skeleton';
import * as Styled from './ProductCard.styles';
export var formatPrice = function formatPrice(price) {
  return price.toFixed(2);
};

var icons = function icons(icon, dataTest) {
  return {
    tick: /*#__PURE__*/React.createElement(AvailableIcon, {
      width: "20",
      height: "20",
      fill: "#008542",
      filled: true,
      "data-test": "".concat(dataTest, "-availabilityIcon-tick")
    }),
    info: /*#__PURE__*/React.createElement(InfoIcon, {
      width: "20",
      height: "20",
      fill: "#428fda",
      "data-test": "".concat(dataTest, "-availabilityIcon-info")
    }),
    out_of_stock: /*#__PURE__*/React.createElement(UnavailableIcon, {
      width: "20",
      height: "20",
      fill: "#D42114",
      "data-test": "".concat(dataTest, "-availabilityIcon-oos")
    }),
    clock: /*#__PURE__*/React.createElement(ClockIcon, {
      width: "20",
      height: "20",
      fill: "#008542",
      "data-test": "".concat(dataTest, "-availabilityIcon-clock")
    }),
    calendar: /*#__PURE__*/React.createElement(CalendarIcon, {
      width: "20",
      height: "20",
      fill: "#008542",
      "data-test": "".concat(dataTest, "-availabilityIcon-calendar")
    })
  }[icon];
};

var ProductCard = function ProductCard(_ref) {
  var _availability$availab;

  var badge = _ref.badge,
      colorVariants = _ref.colorVariants,
      freeDelivery = _ref.freeDelivery,
      hasMultiplePrices = _ref.hasMultiplePrices,
      hasVariations = _ref.hasVariations,
      imageHoverActive = _ref.imageHoverActive,
      isPortrait = _ref.isPortrait,
      isValueMessaging = _ref.isValueMessaging,
      name = _ref.name,
      partNumber = _ref.partNumber,
      price = _ref.price,
      productInfoOnly = _ref.productInfoOnly,
      rating = _ref.rating,
      reviewsCount = _ref.reviewsCount,
      specialOfferText = _ref.specialOfferText,
      wasPrice = _ref.wasPrice,
      brand = _ref.brand,
      lazyImageLoad = _ref.lazyImageLoad,
      featured = _ref.featured,
      cta = _ref.cta,
      turnOffCta = _ref.turnOffCta,
      productLinkOverride = _ref.productLinkOverride,
      wishlistCta = _ref.wishlistCta,
      colorSwatchInline = _ref.colorSwatchInline,
      colorSwatchAlwaysVisible = _ref.colorSwatchAlwaysVisible,
      colorSwatchMaxVariations = _ref.colorSwatchMaxVariations,
      colorSwatchSize = _ref.colorSwatchSize,
      mobileLayout = _ref.mobileLayout,
      onClick = _ref.onClick,
      dataTest = _ref['data-test'],
      availability = _ref.availability,
      energyRating = _ref.energyRating,
      parentPartNumber = _ref.parentPartNumber,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      imageVariant = _useState2[0],
      setImageVariant = _useState2[1];

  var _useState3 = useState(lazyImageLoad),
      _useState4 = _slicedToArray(_useState3, 2),
      firstLoad = _useState4[0],
      setFirstLoad = _useState4[1];

  var _useState5 = useState(lazyImageLoad ? canUseDom() : false),
      _useState6 = _slicedToArray(_useState5, 2),
      imageLoading = _useState6[0],
      setImageLoading = _useState6[1]; // Alt Img Carousel


  var scrollerRef = useRef(null);
  var scrollerItems = 2;

  var _useState7 = useState(0),
      _useState8 = _slicedToArray(_useState7, 2),
      scrollerSlide = _useState8[0],
      setScrollerSlide = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      touchSupport = _useState10[0],
      setTouchSupport = _useState10[1];

  var renderBadges = function renderBadges(key, badgeName) {
    return /*#__PURE__*/React.createElement(Badge, {
      key: key,
      badgeFromUrlType: badgeName,
      badgeFromUrlHeight: "25"
    });
  };

  var onImageMouseEnter = function onImageMouseEnter() {
    if (imageHoverActive && !touchSupport) setImageVariant('/2');
  };

  var onImageMouseLeave = function onImageMouseLeave() {
    if (imageHoverActive && !touchSupport) setImageVariant('');
  };

  var detectTouchSupport = function detectTouchSupport() {
    return canUseDom() && 'ontouchstart' in document.documentElement;
  };

  var imageHeight = isPortrait ? 366 : 270;
  var imageWidth = 270;
  var productLink = productLinkOverride || "/product/".concat(partNumber);

  var onImageLoad = function onImageLoad() {
    setImageLoading(false);

    if (firstLoad && imageHoverActive) {
      setFirstLoad(false);
      var preloadedImage = new Image();
      var dimensions = "w=".concat(imageWidth * 2, "&h=").concat(imageHeight * 2);
      var qualityAndFormat = 'qlt=75&fmt=webp';
      preloadedImage.src = "//media.4rgos.it/s/Argos/".concat(partNumber, "_R_SET/2?").concat(dimensions, "&").concat(qualityAndFormat);
      preloadedImage.srcSet = "//media.4rgos.it/s/Argos/".concat(partNumber, "_R_SET/2?").concat(dimensions, "&").concat(qualityAndFormat);
      return preloadedImage;
    }

    return null;
  };

  var hasReviews = !!rating && !!reviewsCount && reviewsCount > 0;
  var hasWasPrice = wasPrice && wasPrice > 0;
  var isVerticalMobileLayout = mobileLayout === 'vertical'; // Render the button container if it has a CTA/variations/product info only OR it has a wishlist CTA

  var enableButtonContainer = !turnOffCta && (cta || hasVariations || productInfoOnly) || wishlistCta;

  var ctaButton = function ctaButton() {
    return !cta || hasVariations || productInfoOnly ? /*#__PURE__*/React.createElement(Button, {
      as: "a",
      href: productLink,
      onClick: onClick,
      kind: "secondary",
      className: "btn-cta",
      "data-el": hasVariations ? 'variations' : ''
    }, productInfoOnly ? 'More details' : 'Choose options') : cta;
  };

  var handleOnScroll = function handleOnScroll() {
    var slideIndex = Math.round(scrollerRef.current.scrollLeft / scrollerRef.current.scrollWidth * scrollerItems);
    setScrollerSlide(slideIndex);
  };

  useEffect(function () {
    setTouchSupport(detectTouchSupport());
  }, []);
  useEffect(function () {
    if (!touchSupport) return;
    var scroller = scrollerRef.current;

    if (scroller) {
      scroller.addEventListener('scroll', handleOnScroll); // eslint-disable-next-line consistent-return

      return function () {
        return scroller.removeEventListener('scroll', handleOnScroll);
      };
    }
  }, [touchSupport]);
  return /*#__PURE__*/React.createElement(Styled.Wrapper, _extends({
    "data-test": dataTest,
    "data-product-id": partNumber,
    "data-parent-id": parentPartNumber,
    itemScope: true,
    itemType: "http://schema.org/Product"
  }, props), /*#__PURE__*/React.createElement(Styled.ContentBlock, {
    "data-test": "".concat(dataTest, "-contentBlock"),
    mobileLayout: mobileLayout,
    availability: !!availability
  }, /*#__PURE__*/React.createElement(Styled.ImageContainer, {
    onMouseEnter: onImageMouseEnter,
    onMouseLeave: onImageMouseLeave,
    isPortrait: isPortrait,
    "data-test": "".concat(dataTest, "-imageContainer"),
    mobileLayout: mobileLayout
  }, /*#__PURE__*/React.createElement("a", {
    "aria-labelledby": "product-title-".concat(partNumber),
    "data-test": "".concat(dataTest, "-link"),
    target: "_self",
    href: productLink,
    onClick: onClick,
    tabIndex: "-1"
  }, /*#__PURE__*/React.createElement("meta", {
    itemProp: "url",
    content: productLink
  }), touchSupport && imageHoverActive ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Styled.AltImgViewport, {
    width: imageWidth,
    height: imageHeight,
    isPortrait: isPortrait
  }, /*#__PURE__*/React.createElement(Styled.AltImgCarousel, null, /*#__PURE__*/React.createElement(Styled.AltImgScrollOuter, {
    ref: scrollerRef
  }, /*#__PURE__*/React.createElement(ProductImageSkeleton, {
    height: imageHeight,
    isPortrait: isPortrait,
    key: "alt-img-skeleton-".concat(partNumber)
  }), Array.from(Array(scrollerItems)).map(function (slide, index) {
    return /*#__PURE__*/React.createElement(Styled.AltImgScroll, {
      key: "alt-img-scroll-".concat(partNumber, "-").concat(index)
    }, /*#__PURE__*/React.createElement(Styled.ImageWrapper, {
      "data-test": "".concat(dataTest, "-imageWrapper"),
      isPortrait: isPortrait,
      imageLoading: imageLoading
    }, /*#__PURE__*/React.createElement(BoltImage, {
      onLoad: onImageLoad,
      src: "//media.4rgos.it/s/Argos/".concat(partNumber, "_R_SET/").concat(index + 1),
      height: imageHeight,
      width: imageWidth,
      alt: name,
      responsive: true,
      nativeLazy: true
    })));
  })))), /*#__PURE__*/React.createElement(Styled.AltImgIndicators, null, Array.from(Array(scrollerItems)).map(function (btn, index) {
    return /*#__PURE__*/React.createElement(Styled.AltImgIndicatorBtn, {
      key: "alt-img-btn-".concat(partNumber, "-").concat(index),
      onClick: function onClick(event) {
        return event.preventDefault();
      },
      "aria-label": "Scroll to item #".concat(index + 1),
      "aria-pressed": scrollerSlide === index
    });
  }))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ProductImageSkeleton, {
    height: imageHeight,
    isPortrait: isPortrait
  }), /*#__PURE__*/React.createElement(Styled.ImageWrapper, {
    "data-test": "".concat(dataTest, "-imageWrapper"),
    isPortrait: isPortrait,
    imageLoading: imageLoading
  }, /*#__PURE__*/React.createElement(BoltImage, {
    onLoad: onImageLoad,
    src: "//media.4rgos.it/s/Argos/".concat(partNumber, "_R_SET").concat(imageVariant),
    height: imageHeight,
    width: imageWidth,
    alt: name,
    responsive: true,
    lazy: lazyImageLoad
  })))), isVerticalMobileLayout && wishlistCta && /*#__PURE__*/React.createElement("div", {
    "data-test": "".concat(dataTest, "-image-wishlist-cta"),
    className: "xs-block md-hidden"
  }, /*#__PURE__*/React.createElement(Styled.WishlistIconContainer, {
    mobileLayout: mobileLayout
  }, /*#__PURE__*/React.cloneElement(wishlistCta, {
    placement: 'xs'
  }))), featured && /*#__PURE__*/React.createElement(Styled.FlyoutBadge, {
    color: "#ffffff",
    backgroundColor: "#707070",
    flyout: true
  }, "SPONSORED")), /*#__PURE__*/React.createElement(Styled.TextContainer, {
    "data-test": "".concat(dataTest, "-textContainer"),
    mobileLayout: mobileLayout
  }, badge && /*#__PURE__*/React.createElement(Styled.Link, {
    href: productLink,
    onClick: onClick,
    target: "_self",
    itemProp: "name"
  }, /*#__PURE__*/React.createElement(Styled.BadgeContainer, {
    mobileLayout: mobileLayout
  }, Object.entries(badge).map(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
        key = _ref3[0],
        badgeName = _ref3[1];

    return badgeName[0] && renderBadges(key, badgeName[0].toString());
  }))), /*#__PURE__*/React.createElement(Styled.BrandLogoContainer, {
    colorSwatchInline: colorSwatchInline,
    "data-test": "".concat(dataTest, "-brand")
  }, !!brand && (typeof brand === 'string' ? /*#__PURE__*/React.createElement("strong", null, brand) : brand), colorVariants && /*#__PURE__*/React.createElement(Styled.BrandedColorSwatchList, {
    colors: colorVariants.map(function (variant) {
      return variant;
    }),
    maxVariations: colorSwatchMaxVariations,
    swatchAnchorLink: !!colorVariants.length,
    "data-test": "".concat(dataTest, "-color-swatch"),
    hasBrand: !!brand,
    isFixed: colorSwatchAlwaysVisible,
    size: colorSwatchSize,
    colorSwatchInline: colorSwatchInline
  })), /*#__PURE__*/React.createElement(Styled.Link, {
    id: "product-title-".concat(partNumber),
    href: productLink,
    onClick: onClick,
    "data-test": "".concat(dataTest, "-title-link"),
    target: "_self",
    itemProp: "name"
  }, /*#__PURE__*/React.createElement("meta", {
    itemProp: "url",
    content: productLink
  }), /*#__PURE__*/React.createElement(Styled.Title, {
    "data-test": "".concat(dataTest, "-title")
  }, name), hasReviews && /*#__PURE__*/React.createElement(Ratings, {
    rating: rating,
    reviewsCount: reviewsCount
  })), (energyRating === null || energyRating === void 0 ? void 0 : energyRating.value) && /*#__PURE__*/React.createElement(Styled.EnergyLabelWrapper, null, /*#__PURE__*/React.createElement(EnergyLabelBadge, {
    "data-test": "energy-rating",
    labelHeight: 25,
    labelWidth: 45,
    type: energyRating === null || energyRating === void 0 ? void 0 : energyRating.value,
    linkUrl: energyRating === null || energyRating === void 0 ? void 0 : energyRating.pdf,
    onClick: energyRating === null || energyRating === void 0 ? void 0 : energyRating.energyLabelhandler
  })), isValueMessaging ? /*#__PURE__*/React.createElement(Styled.Link, {
    href: productLink,
    onClick: onClick,
    target: "_self"
  }, /*#__PURE__*/React.createElement(Styled.ValueMessagingContainer, {
    "data-test": "".concat(dataTest, "-valueMessagingContainer")
  }, specialOfferText && /*#__PURE__*/React.createElement(Styled.SavingTextContainer, {
    "data-test": "".concat(dataTest, "-savingTextContainer"),
    isValueMessaging: true
  }, /*#__PURE__*/React.createElement(Styled.SavingText, {
    "data-test": "special-offer",
    isValueMessaging: true
  }, specialOfferText)), /*#__PURE__*/React.createElement(Styled.PriceContainer, {
    "data-test": "".concat(dataTest, "-priceContainer"),
    isValueMessaging: true
  }, price > 0 && /*#__PURE__*/React.createElement(Styled.PriceText, {
    "data-test": "".concat(dataTest, "-price"),
    "data-el": hasMultiplePrices ? 'from-price' : '',
    isValueMessaging: true,
    hasWasPrice: hasWasPrice
  }, hasMultiplePrices && 'From', " ", /*#__PURE__*/React.createElement("strong", null, "\xA3".concat(formatPrice(price))), hasWasPrice && /*#__PURE__*/React.createElement(Styled.PriceDivider, null)), hasWasPrice && /*#__PURE__*/React.createElement(Styled.WasText, {
    "data-test": "".concat(dataTest, "-wasPrice"),
    isValueMessaging: true
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, "Was\xA0"), " ", "\xA3".concat(formatPrice(wasPrice)))))) : /*#__PURE__*/React.createElement(Styled.Link, {
    href: productLink,
    onClick: onClick,
    target: "_self"
  }, /*#__PURE__*/React.createElement(Styled.PriceContainer, {
    "data-test": "".concat(dataTest, "-priceContainer")
  }, price > 0 && /*#__PURE__*/React.createElement(Styled.PriceText, {
    "data-test": "".concat(dataTest, "-price"),
    "data-el": hasMultiplePrices ? 'from-price' : ''
  }, hasMultiplePrices && 'From', " ", /*#__PURE__*/React.createElement("strong", null, "\xA3".concat(formatPrice(price))))), /*#__PURE__*/React.createElement(Styled.SavingTextContainer, {
    "data-test": "".concat(dataTest, "-savingTextContainer")
  }, specialOfferText && /*#__PURE__*/React.createElement(Styled.SavingText, {
    "data-test": "special-offer"
  }, specialOfferText), hasWasPrice && /*#__PURE__*/React.createElement(Styled.WasText, {
    "data-test": "".concat(dataTest, "-wasPrice")
  }, "Was \xA3".concat(formatPrice(wasPrice))))), freeDelivery && /*#__PURE__*/React.createElement(Styled.Link, {
    href: productLink,
    onClick: onClick,
    target: "_self"
  }, /*#__PURE__*/React.createElement(Styled.DeliveryText, {
    "data-test": "".concat(dataTest, "-freeDelivery")
  }, "Free delivery")))), availability && /*#__PURE__*/React.createElement(Styled.Link, {
    href: productLink,
    onClick: onClick,
    target: "_self"
  }, /*#__PURE__*/React.createElement(Styled.AvailabilityLabelContainer, {
    "data-test": "".concat(dataTest, "-availabilityContainer")
  }, availability.loading ? /*#__PURE__*/React.createElement(Styled.AvailabilityLoadingContainer, null, /*#__PURE__*/React.createElement(RectangleSkeleton, {
    height: 32
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, ((_availability$availab = availability.availabilityLabels) === null || _availability$availab === void 0 ? void 0 : _availability$availab.length) > 0 && availability.availabilityLabels.map(function (_ref4, index) {
    var icon = _ref4.icon,
        message = _ref4.message;
    return /*#__PURE__*/React.createElement(Styled.AvailabilityLabelWrapper, {
      key: "".concat(icon, "-").concat(index)
    }, /*#__PURE__*/React.createElement(Styled.AvailabilityLabel, {
      "data-test": "".concat(dataTest, "-availabilityLabel"),
      hasBackgroundColor: availability === null || availability === void 0 ? void 0 : availability.hasBackgroundColor,
      icon: icon
    }, icons(icon, dataTest), message));
  })))), enableButtonContainer && /*#__PURE__*/React.createElement(Styled.ButtonContainer, {
    isPortrait: isPortrait,
    mobileLayout: mobileLayout
  }, !turnOffCta && ctaButton(), wishlistCta && /*#__PURE__*/React.createElement("div", {
    "data-test": "".concat(dataTest, "-wishlist-cta"),
    className: "xs-".concat(isVerticalMobileLayout ? 'hidden' : 'block', " md-block") // Hide on mobile if vertical mobile layout as we show the image wishlist cta

  }, /*#__PURE__*/React.createElement(Styled.WishlistIconContainer, {
    mobileLayout: mobileLayout
  }, wishlistCta))));
};

ProductCard.displayName = 'ProductCard';
ProductCard.propTypes = {
  /**
   * Optional object of badges for the product
   */
  badge: PropTypes.PropTypes.shape({
    BADGE_VALUE: PropTypes.array,
    BADGE_PRODUCT: PropTypes.array,
    BADGE_DELIVERY: PropTypes.array
  }),

  /**
   * Optional array to show color variants of the product
   */
  colorVariants: PropTypes.array,

  /**
   * Optional prop to specify if the product is eligible for free Delivery
   */
  freeDelivery: PropTypes.bool,

  /**
   * Optional prop to specify whether the product has multiple prices (display 'From' before product price)
   */
  hasMultiplePrices: PropTypes.bool,

  /**
   * Optional prop to specify whether the product has variations
   */
  hasVariations: PropTypes.bool,

  /**
   * Optional prop to specify whether image hover is active
   */
  imageHoverActive: PropTypes.bool,

  /**
   * Optional prop to specify if the product is of type clothing
   */
  isPortrait: PropTypes.bool,

  /**
   * Optional prop to specify if the product should display value messaging (for lister pages)
   */
  isValueMessaging: PropTypes.bool,

  /**
   * Required name of product
   */
  name: PropTypes.string.isRequired,

  /**
   * Required partNumber of product
   */
  partNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,

  /**
   * Required price of product
   */
  price: PropTypes.number.isRequired,

  /**
   * Optional bool to change the CTA button text to "more options"
   */
  productInfoOnly: PropTypes.bool,

  /**
   * Optional rating value of product
   */
  rating: PropTypes.number,

  /**
   * Optional number of reviews of product
   */
  reviewsCount: PropTypes.number,

  /**
   * Optional special offer text (will be displayed in red)
   */
  specialOfferText: PropTypes.string,

  /**
   * Optional wasPrice of product
   */
  wasPrice: PropTypes.number,

  /**
   * Optional brand of product
   */
  brand: PropTypes.node,

  /**
   * Optional link of product
   */
  productLinkOverride: PropTypes.string,

  /**
   * Optional boolean to specify if image shuold lazily load
   */
  lazyImageLoad: PropTypes.bool,

  /**
   * Optional boolean to specify if image shuold have the featured banner
   */
  featured: PropTypes.bool,

  /**
   * Optional prop to pass through the main call to action
   */
  cta: PropTypes.node,

  /**
   * Optional bool to turn off the main product card CTA
   */
  turnOffCta: PropTypes.bool,

  /**
   * Optional prop to pass through the wishlist call to action
   */
  wishlistCta: PropTypes.node,

  /**
   * Optional prop to specify if the color swatch list should be rendered inline
   */
  colorSwatchInline: PropTypes.bool,

  /**
   * Optional prop to specify if the color swatch list should be visible on any screens
   */
  colorSwatchAlwaysVisible: PropTypes.bool,

  /**
   * Optional prop to specify the maximum amount of color swatches to be rendered
   */
  colorSwatchMaxVariations: PropTypes.number,

  /**
   * Optional prop to specify the size of the color swatches
   */
  colorSwatchSize: PropTypes.oneOf(['small', 'medium']),

  /**
   * Optional prop to specify which layout to use on mobile and tablet viewports
   */
  mobileLayout: PropTypes.oneOf(['horizontal', 'horizontal-even', 'vertical']),

  /**
   * Optional prop to pass through onClick events when Img or Title is clicked
   */
  onClick: PropTypes.func,

  /**
   * Specify an optional data-test attribute to be added to your Image for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Optional prop to specify the parent product number for variants
   */
  parentPartNumber: PropTypes.string,

  /**
   * Optional object to show availability of the product
   */
  availability: PropTypes.shape({
    loading: PropTypes.bool,
    availabilityLabels: PropTypes.arrayOf(PropTypes.shape({
      icon: PropTypes.string,
      message: PropTypes.string
    })),
    hasBackgroundColor: PropTypes.bool
  }),

  /**
   * Optional object to show Energy Rating of the product
   */
  energyRating: PropTypes.shape({
    value: PropTypes.string,
    pdf: PropTypes.string,
    energyLabelhandler: PropTypes.func
  })
};
ProductCard.defaultProps = {
  wasPrice: null,
  rating: null,
  reviewsCount: null,
  colorVariants: null,
  freeDelivery: false,
  specialOfferText: null,
  badge: null,
  hasVariations: false,
  imageHoverActive: false,
  hasMultiplePrices: false,
  isPortrait: false,
  isValueMessaging: false,
  brand: null,
  lazyImageLoad: true,
  featured: false,
  productInfoOnly: false,
  cta: null,
  turnOffCta: false,
  wishlistCta: null,
  productLinkOverride: null,
  colorSwatchInline: true,
  colorSwatchAlwaysVisible: false,
  colorSwatchMaxVariations: 4,
  colorSwatchSize: 'small',
  mobileLayout: 'horizontal',
  onClick: function onClick() {},
  'data-test': 'component-product-card',
  parentPartNumber: null,
  availability: null,
  energyRating: null
};
export default ProductCard;